import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import 'react-bootstrap-range-slider/dist/react-bootstrap-range-slider.css'
import RangeSlider from 'react-bootstrap-range-slider'
import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'
import NumberFormat from 'react-number-format'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import { validateUEN } from '../../../../_metronic/helpers/validation'
import { fetchStartWithForward, fetchStop } from '../../../../redux/actions/common'
import {
  saveApplicationDetails,
  setLoanApplication,
  setLoanEligibility,
  setLoanStep,
  setLoanSubStep,
} from '../redux/actions'
import { privacyPolicy, trim } from '../../../../_metronic/helpers'
import { MaintenanceModeBanner } from '../../../../_metronic/helpers/components/MaintenanceModeBanner'
import { getStoredEnv } from '../../../../utils/getIntegrationURLFromENV'
import { RightAeroIcon } from '../../../../svg/RightAeroIcon'
import { useHistory } from 'react-router-dom'
import { OverlayTrigger } from 'react-bootstrap-v5'
import { Popover } from 'react-bootstrap'
import { AlexiApplicationTypes } from '../../../../utils/constants/AlexiApplicationType.enum'
import { LeftArowIcon } from '../../../../svg/LeftArowIcon'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { parsePhoneNumberWithError } from 'libphonenumber-js/max'
import { scrollToErros } from '../../loanApplication/steps/Step1Sub1'
import { Autocomplete, Box, TextField } from '@mui/material'
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined'
import { CountryList } from '../../../../utils/constants/country-list'
import { ServerError, useMutation } from '@apollo/client'
import mutations from '../../../../setup/graphql/mutations'
import Alert from '../../../../_metronic/partials/alert'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import '../login.css'

const calendarIconStyles = `
  .date-picker-wrapper {
    position: relative;
    width: 100%;
    cursor: pointer;
  }
  .calendar-icon-right {
    position: absolute !important;
    right: 10px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    pointer-events: none;
    z-index: 2;
    cursor: pointer;
  }
  .react-datepicker__input-container input {
    width: 100%;
    padding-right: 30px;
  }
`

const industryOptions = [
  { label: 'Service Sector – F&B', value: 'service-sector-fb', rating: 'H', pricing: '21' },
  { label: 'Service Sector – non-F&B', value: 'service-sector-non-fb', rating: 'M', pricing: '15' },
  { label: 'Health & Human Services', value: 'health-human-services', rating: 'M', pricing: '15' },
  { label: 'Education', value: 'education', rating: 'L', pricing: '8' },
  { label: 'IT services', value: 'it-services', rating: 'L', pricing: '8' },
  { label: 'Media', value: 'media', rating: 'M', pricing: '15' },
  {
    label: 'Agriculture / Fishing / Food Production',
    value: 'agriculture-fishing-foodproduction',
    rating: 'H',
    pricing: '21',
  },
  { label: 'Wholesale Trader', value: 'wholesale-trader', rating: 'M', pricing: '15' },
  { label: 'Retail Trading', value: 'retail-trading', rating: 'M', pricing: '15' },
  {
    label: 'Manufacturing – heavy industries',
    value: 'manufacturing-heavy-industries',
    rating: 'M',
    pricing: '15',
  },
  {
    label: 'Manufacturing – consumer related',
    value: 'manufacturing-consumer-related',
    rating: 'M',
    pricing: '15',
  },
  {
    label: 'Manufacturing – computer & electronics',
    value: 'manufacturing-computer-electronics',
    rating: 'L',
    pricing: '8',
  },
  {
    label: 'Energy – generation and distribution',
    value: 'energy-generation-distribution',
    rating: 'H',
    pricing: '21',
  },
  { label: 'Transport & Logistics', value: 'transport-logistics', rating: 'M', pricing: '15' },
  { label: 'Renewable', value: 'renewable', rating: 'M', pricing: '15' },
  {
    label: 'Building and Construction - Private Sector',
    value: 'building-construction-private',
    rating: 'H',
    pricing: '21',
  },
  {
    label: 'Building and Construction - Public Sector',
    value: 'building-construction-public',
    rating: 'L',
    pricing: '8',
  },
  { label: 'Other', value: 'other', rating: 'M', pricing: '15' },
]

const loanPurpose = [
  { label: 'Business Expansion', value: 'business-expansion' },
  { label: 'Pay Invoices', value: 'pay-invoices' },
  { label: 'Equipment Purchase', value: 'equipment-purchase' },
  { label: 'Infrastructure Upgrades', value: 'infrastructure-upgrades' },
  { label: 'Working Capital', value: 'working-capital' },
  { label: 'Other', value: 'other' },
]

const errorMsg = {
  required: 'required',
  amountRange: 'Amount range should be between 100,000 - 3,000,000',
  durationRange: 'Duration range should be between 3 - 36 months',
}

const Step1Sub1: FC<any> = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const maxAmount = 3000000
  const minAmount = 100000
  const maxDuration = 36
  const minDuration = 3
  const today = new Date()
  const agentCode = localStorage.getItem('agentCode')

  const [loanAmountRange, setLoanAmountRange] = useState<any>(100000)
  const [loanDurationRange, setLoanDurationRange] = useState<any>(6)
  const [countryOfIncorporationCode, setCountryOfIncorporationCode] = useState<any>(null)
  const [showAlert, setShowAlert] = useState<boolean>(false)
  const [alertMsg, setAlertMsg] = useState<string>('')
  const [alertType, setAlertType] = useState<any>('primary')
  const [mobileNo, setMobileNo] = useState<any>('')
  const loanApplication = useSelector((state: any) => state.loan.loanApplication)
  const loanEligibility = useSelector((state: any) => state.loan.loanEligibility)

  const [createLoanElgibility, onSuccessCreateLoanElgibility] = useMutation(
    mutations.CREATE_LOAN_ELIGIBILITY
  )

  const [updateLoanElgibility, onSuccessUpdateLoanElgibility] = useMutation(
    mutations.UPDATE_LOAN_ELIGIBILITY
  )

  const [updateApplication, onSuccessUpdateApplicaton] = useMutation(mutations.UPDATE_APPLICATION)

  const [createApplication, onSuccessCreateApplicaton] = useMutation(mutations.CREATE_APPLICATION)

  // const [mobileNo, setMobileNo] = useState<any>('+977984');
  // const [sendOTP, {data,loading,error}] = useMutation(mutations.SEND_OTP, { variables: { mobileNo, isApplicantOrShareholder: true } });

  const savedApplicationDetails = useSelector((state: any) => state.loan.loanApplication)

  const handleLoanAmountRange = (value: string) => {
    dispatch(saveApplicationDetails(formik.values))
    // dispatch(setLoanApplication(formik.values))
    // setLoanAmountRange(value)
    formik.setFieldValue('amountRequested', value)
    // if(error.match(/ name/i)) formik.setFieldError('name', error);
  }

  const handleLoanDurationRange = (value: string) => {
    dispatch(saveApplicationDetails(formik.values))
    // dispatch(setLoanApplication(formik.values))
    // setLoanDurationRange(value)
    formik.setFieldValue('durationInMonths', value)
  }

  const initialValues = {
    applicantName: loanApplication?.applicantName || '',
    // applicationStatus: '',
    loanPurpose: loanEligibility?.loanPurpose || '',
    companyUEN: loanApplication?.companyUEN || '',
    positionInCompany: loanApplication?.positionInCompany || '',
    amountRequested: loanApplication ? loanApplication?.amountRequested : loanAmountRange,
    durationInMonths: loanApplication ? loanApplication?.durationInMonths : loanDurationRange,
    mobileNumber: loanApplication?.mobileNumber || '',
    promoCode: loanApplication?.promoCode || '',
    businessOwnerEmail: loanEligibility?.businessEmailAddress || '',
    mibCountryOfIncorporation: loanApplication?.mibCountryOfIncorporation || '',
    dateOfIncorporation: loanEligibility ? new Date(loanEligibility?.dateOfIncorporation) : null,
    companyName: loanEligibility?.companyName || '',
    industryType: loanEligibility?.industryType || '',
    turnover: loanEligibility?.turnover || 0,
    profit: loanEligibility?.profit || 0,
    totalMonthlyInstallmentsToBanks: loanEligibility?.totalMonthlyInstallmentsToBanks || 0,
    totalMonthlyInstallmentsToNonBanks: loanEligibility?.totalMonthlyInstallmentsToNonBanks || 0,
    totalRevolvingCreditOutstandingToBanks:
      loanEligibility?.totalRevolvingCreditOutstandingToBanks || 0,
    totalRevolvingCreditOutstandingToNonBanks:
      loanEligibility?.totalRevolvingCreditOutstandingToNonBanks || 0,
    currentLitigationAmount: loanEligibility?.currentLitigationAmount || 0,
    anyOutstandingDebts: loanEligibility?.anyOutstandingDebts || false,
    confirmCheckbox: false,
    validDataCheckbox: false,
    confirmCheckboxConsent: false,
  }

  const validationSchema = Yup.object().shape({
    loanPurpose: Yup.string()
      .required('Please select a Loan Purpose')
      .test('mustSelect', 'Please select a Loan Purpose', (value: any) => {
        return value !== '-'
      }),
    amountRequested: Yup.number()
      .required(errorMsg.required)
      .min(minAmount, errorMsg.amountRange)
      .max(maxAmount, errorMsg.amountRange),
    durationInMonths: Yup.number()
      .required(errorMsg.required)
      .min(minDuration, errorMsg.durationRange)
      .max(maxDuration, errorMsg.durationRange),
    companyUEN: Yup.string()
      .required('Company UEN is required')
      .test('unique', 'Invalid UEN. Kindly recheck the entered UEN', (value: any) =>
        validateUEN(value)
      ),
    applicantName: Yup.string()
      .required('Business Owner Name is required')
      .matches(
        /^[A-Za-z\s.']+$/,
        'Please enter a valid Business Owner Name using letters, spaces, periods, or apostrophes, and include at least 2 letters.'
      )
      .test(
        'at-least-two-letters',
        'Business Owner Name cannot contain only special characters. Please enter a valid business owner name.',
        (value: any) => {
          if (!value) return false
          const letters = value.match(/[A-Za-z]/g)
          return (letters?.length || 0) >= 2
        }
      ),
    mibCountryOfIncorporation: Yup.string().required('Country of Incorporation is required'),
    businessOwnerEmail: Yup.string()
      .required('Business Owner Email Address is required')
      .email('Enter Valid Business Owner Email'),
    companyName: Yup.string()
      .required('Company Name is required')
      .min(3, 'Company Name must be at least 3 characters')
      .max(40, 'Company Name must not exceed 40 characters')
      .test(
        'no-leading-trailing-spaces',
        'Company Name cannot have leading or trailing spaces',
        (value) => !value || value === value.trim()
      )
      .test(
        'contains-letters',
        'Company Name must contain at least one letter',
        (value) => !value || /[a-zA-Z]/.test(value)
      )
      .test(
        'not-only-numbers',
        'Company Name cannot contain only numbers',
        (value) => !value || !/^[0-9\s]+$/.test(value)
      )
      .test(
        'not-only-special-chars',
        'Company Name cannot contain only special characters',
        (value) => !value || /[a-zA-Z0-9]/.test(value)
      )
      .matches(
        /^[a-zA-Z0-9\s\-&',.()/]+$/,
        "Company Name can only contain letters, numbers, spaces, and these special characters: -&',.()/"
      ),
    promoCode: Yup.string(),
    dateOfIncorporation: Yup.string()
      .required('Date of Incorporation is required')
      .typeError('Date of Incorporation is required'),
    industryType: Yup.string()
      .required('Industry is required')
      .test('mustSelectType', 'Please select an Industry Type', (value: any) => {
        return value !== '-'
      }),
    mobileNumber: Yup.string()
      .required('Business Mobile Number is required')
      .test('mobileValidation', 'Please enter valid Singapore number', (value: any) => {
        if (!value) return false
        // Use libphonenumber-js to validate phone number format
        const number = parsePhoneNumberWithError(value, 'SG')
        // Use libphonenumber-js to validate phone number format
        return isValidPhoneNumber(value, 'SG') && number.isValid() && number.country === 'SG'
      }),
    turnover: Yup.number()
      .typeError('Annual turnover cannot be less than 0')
      .test('notlessThanZero', 'Annual turnover should be greater than 0', (value: any) => {
        return value > 0
      })
      .test('turnOverNotZero', 'Annual Turnover cannot be 0', (value: any) => {
        return value !== 0
      }),
    profit: Yup.number()
      .typeError('Net Operating income cannot be less than 0')
      .test('lessthanZero', 'Net Operating income should be greater than 0', (value: any) => {
        return value > 0
      })
      .test('notZero', 'Net Operating income should be greater than 0', (value: any) => {
        return value !== 0
      }),
    anyOutstandingDebts: Yup.boolean().required(errorMsg.required),
    totalMonthlyInstallmentsToBanks: Yup.number().when('anyOutstandingDebts', {
      is: true,
      then: Yup.number()
        .typeError('Total Monthly Installments To Banks cannot be less than 0')
        .test(
          'installment',
          'Total Monthly Installments To Banks cannot be less than 0',
          (value: any) => value >= 0
        ),
    }),

    totalMonthlyInstallmentsToNonBanks: Yup.number().when('anyOutstandingDebts', {
      is: true,
      then: Yup.number()
        .typeError('Total Monthly Installments To Non Banks cannot be less than 0')
        .test(
          'nonBanks',
          'Total Monthly Installments To Non Banks cannot be less than 0',
          (value: any) => value >= 0
        ),
    }),

    totalRevolvingCreditOutstandingToBanks: Yup.number().when('anyOutstandingDebts', {
      is: true,
      then: Yup.number()
        .typeError('Total Revolving Credit Outstanding To Banks cannot be less than 0')
        .test(
          'creditOutstanding',
          'Total Revolving Credit Outstanding To Banks cannot be less than 0',
          (value: any) => value >= 0
        ),
    }),

    totalRevolvingCreditOutstandingToNonBanks: Yup.number().when('anyOutstandingDebts', {
      is: true,
      then: Yup.number()
        .typeError('Total Revolving Credit Outstanding To Non Banks cannot be less than 0')
        .test(
          'revolving',
          'Total Revolving Credit Outstanding To Non Banks cannot be less than 0',
          (value: any) => value >= 0
        ),
    }),

    currentLitigationAmount: Yup.number().when('anyOutstandingDebts', {
      is: true,
      then: Yup.number()
        .typeError('Current Litigation Amount cannot be less than 0')
        .test(
          'currentLitigationAmount',
          'Current Litigation Amount cannot be less than 0',
          (value: any) => value >= 0
        ),
    }),
    confirmCheckbox: Yup.boolean()
      .oneOf([true], 'Please consent to above mandatory terms')
      .required('Please consent to above mandatory terms'),
    validDataCheckbox: Yup.boolean()
      .oneOf([true], 'Please consent to above mandatory terms')
      .required('Please consent to above mandatory terms'),
    confirmCheckboxConsent: Yup.boolean()
      .oneOf([true], 'Please consent to above mandatory terms')
      .required('Please consent to above mandatory terms'),
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      try {
        const {
          amountRequested,
          durationInMonths,
          companyUEN,
          positionInCompany,
          applicantName,
          mobileNumber,
          promoCode,
          businessOwnerEmail,
          mibCountryOfIncorporation,
        } = trim(values)

        dispatch(fetchStartWithForward())

        if (
          localStorage.getItem('loanType') === null ||
          localStorage.getItem('loanType') === undefined
        ) {
          localStorage.setItem('loanType', AlexiApplicationTypes.LOAN_APPLICATION)
          localStorage.setItem('applicationType', AlexiApplicationTypes.AGENT_CLIENT_ONBOARDING)
        }
        if (
          savedApplicationDetails === null ||
          savedApplicationDetails?.id === null ||
          savedApplicationDetails?.id === undefined
        ) {
          await createApplication({
            variables: {
              data: {
                amountRequested: amountRequested * 1,
                durationInMonths: durationInMonths * 1,
                companyUEN,
                mibCompanyUEN: companyUEN,
                mibCountryOfIncorporation,
                agentCode: agentCode,
                applicantName,
                businessOwnerEmail,
                mobileNumber,
                promoCode,
                applicationStatus: 'in-progress',
                applicationCompleteStep: 1.1,
                applicationType:
                  localStorage.getItem('loanType') || localStorage.getItem('applicationType') || AlexiApplicationTypes.AGENT_CLIENT_ONBOARDING,
                partnerId: localStorage.getItem('partnerId') || '',
                credilinqEnvironment: getStoredEnv(),
              },
            },
          })
        } else {
          await updateApplication({
            variables: {
              data: {
                amountRequested: amountRequested * 1,
                durationInMonths: durationInMonths * 1,
                companyUEN,
                mibCountryOfIncorporation,
                mibCompanyUEN: companyUEN,
                agentCode: agentCode,
                positionInCompany,
                applicantName,
                businessOwnerEmail,
                mobileNumber,
                promoCode,
              },
              where: {
                id: savedApplicationDetails?.id,
              },
            },
          })
        }
      } catch (error) {
        console.error('Error creating application:', error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    if (onSuccessCreateApplicaton.called && !onSuccessCreateApplicaton.loading) {
      dispatch(fetchStop())
      if (onSuccessCreateApplicaton.data) {
        const { createApplication } = onSuccessCreateApplicaton.data
        const mobileNo = createApplication.mobileNumber

        /**
         * Resetting Mail Sent Flag on New Application Creation
         */
        dispatch(setLoanApplication(createApplication))
        localStorage.removeItem('errorLogged')
        setMobileNo(mobileNo)
        if (createApplication && createApplication?.companyUEN) {
          if (
            ['stage', 'sandbox', 'dev', 'local'].includes(localStorage.getItem('env') as string)
          ) {
            ;(async () => {
              const response = await import(
                `../../../../utils/custom/${createApplication.companyUEN}/step1Sub3.json`
              )
              const eligibilityData: any = response.default
              if (eligibilityData) {
                // Dispatch the action to update the loan eligibility in the Redux store
                dispatch(setLoanEligibility(eligibilityData))
              }
            })()
          }
          localStorage.setItem('companyUEN', createApplication.companyUEN)

          setTimeout(() => {
            history.push(`/agent-application/${createApplication.id}?step=2`)
            dispatch(setLoanStep(2))
            dispatch(setLoanSubStep(0))
          }, 500)
        }

        const values = trim(formik.values)
        const {
          anyOutstandingDebts,
          totalMonthlyInstallmentsToBanks,
          totalMonthlyInstallmentsToNonBanks,
          totalRevolvingCreditOutstandingToBanks,
          totalRevolvingCreditOutstandingToNonBanks,
          currentLitigationAmount,
          companyName,
          dateOfIncorporation,
          loanPurpose,
          industryType,
          turnover,
          profit,
        } = values
        //creating loan eligiblity based on application ID and provided data
        createLoanElgibility({
          variables: {
            data: {
              companyName,
              dateOfIncorporation,
              loanPurpose,
              industryType,
              turnover: parseInt(turnover),
              profit: parseInt(profit),
              totalMonthlyInstallmentsToBanks: anyOutstandingDebts
                ? parseInt(totalMonthlyInstallmentsToBanks)
                : 0,
              totalMonthlyInstallmentsToNonBanks: anyOutstandingDebts
                ? parseInt(totalMonthlyInstallmentsToNonBanks)
                : 0,
              totalRevolvingCreditOutstandingToBanks: anyOutstandingDebts
                ? parseInt(totalRevolvingCreditOutstandingToBanks)
                : 0,
              totalRevolvingCreditOutstandingToNonBanks: anyOutstandingDebts
                ? parseInt(totalRevolvingCreditOutstandingToNonBanks)
                : 0,
              currentLitigationAmount: anyOutstandingDebts ? parseInt(currentLitigationAmount) : 0,
              anyOutstandingDebts: anyOutstandingDebts,
              businessMobileNumber: createApplication?.mobileNumber,
              businessEmailAddress: createApplication?.businessOwnerEmail,
              application: {
                connect: {
                  id: createApplication?.id,
                },
              },
            },
          },
        })
        onSuccessCreateApplicaton.reset()
      } else {
      }

      if (onSuccessCreateApplicaton.error) {
        const { networkError } = onSuccessCreateApplicaton.error
        let errorList = 'ERROR: '
        if (networkError && (networkError as ServerError)) {
          errorList =
            errorList +
            (networkError as ServerError as any).result.errors
              .map((error: any) => error.message)
              .join(', ')
        }
        setShowAlert(true)
        setAlertMsg(errorList)
        setAlertType('primary')
        dispatch(fetchStop())
      }
    }
  }, [
    onSuccessCreateApplicaton.loading,
    onSuccessCreateApplicaton.data,
    onSuccessCreateApplicaton.error,
    onSuccessCreateApplicaton.called,
  ])

  useEffect(() => {
    if (onSuccessCreateLoanElgibility.called && !onSuccessCreateLoanElgibility.loading) {
      dispatch(fetchStop())
      if (onSuccessCreateLoanElgibility.data) {
        if (onSuccessCreateLoanElgibility.data) {
          const { createLoanEligibility } = onSuccessCreateLoanElgibility.data
          // after creation updating the global state of the loan eligibility and navigating to step 2
          dispatch(setLoanEligibility(createLoanEligibility))
          dispatch(setLoanStep(2))
          history.replace(`/agent-application/${savedApplicationDetails.id}?step=2`)
          onSuccessCreateLoanElgibility.reset()
        }
      } else {
      }

      if (onSuccessCreateLoanElgibility.error) {
        const { networkError } = onSuccessCreateLoanElgibility.error
        let errorList = 'ERROR: '
        if (networkError && (networkError as ServerError)) {
          errorList =
            errorList +
            (networkError as ServerError as any).result.errors
              .map((error: any) => error.message)
              .join(', ')
        }
        setShowAlert(true)
        setAlertMsg(errorList)
        setAlertType('primary')
        dispatch(fetchStop())
      }
    }
  }, [
    onSuccessCreateLoanElgibility.loading,
    onSuccessCreateLoanElgibility.data,
    onSuccessCreateLoanElgibility.error,
    onSuccessCreateLoanElgibility.called,
  ])

  useEffect(() => {
    if (onSuccessUpdateLoanElgibility.called && !onSuccessUpdateLoanElgibility.loading) {
      dispatch(fetchStop())
      if (onSuccessUpdateLoanElgibility.data) {
        if (onSuccessUpdateLoanElgibility.data) {
          const { updateLoanEligibility } = onSuccessUpdateLoanElgibility.data

          dispatch(setLoanEligibility(updateLoanEligibility))
          dispatch(setLoanStep(2))
          history.replace(`/agent-application/${savedApplicationDetails.id}?step=2`)
          onSuccessUpdateLoanElgibility.reset()
        }
      } else {
      }

      if (onSuccessUpdateLoanElgibility.error) {
        const { networkError } = onSuccessUpdateLoanElgibility.error
        let errorList = 'ERROR: '
        if (networkError && (networkError as ServerError)) {
          errorList =
            errorList +
            (networkError as ServerError as any).result.errors
              .map((error: any) => error.message)
              .join(', ')
        }
        setShowAlert(true)
        setAlertMsg(errorList)
        setAlertType('primary')
        dispatch(fetchStop())
      }
    }
  }, [
    onSuccessUpdateLoanElgibility.loading,
    onSuccessUpdateLoanElgibility.data,
    onSuccessUpdateLoanElgibility.error,
    onSuccessUpdateLoanElgibility.called,
  ])

  useEffect(() => {
    if (onSuccessUpdateApplicaton.called && !onSuccessUpdateApplicaton.loading) {
      // dispatch(fetchStop());
      if (onSuccessUpdateApplicaton.data) {
        const { updateApplication } = onSuccessUpdateApplicaton.data
        const application = { ...loanApplication, ...updateApplication }
        dispatch(setLoanApplication(application))
        if (application?.loanEligibility?.id) {
          const values = trim(formik.values)
          const {
            anyOutstandingDebts,
            totalMonthlyInstallmentsToBanks,
            totalMonthlyInstallmentsToNonBanks,
            totalRevolvingCreditOutstandingToBanks,
            totalRevolvingCreditOutstandingToNonBanks,
            currentLitigationAmount,
            companyName,
            dateOfIncorporation,
            loanPurpose,
            industryType,
            turnover,
            profit,
          } = values

          updateLoanElgibility({
            variables: {
              data: {
                companyName,
                dateOfIncorporation,
                loanPurpose,
                industryType,
                turnover: parseInt(turnover),
                profit: parseInt(profit),
                totalMonthlyInstallmentsToBanks: anyOutstandingDebts
                  ? parseInt(totalMonthlyInstallmentsToBanks)
                  : 0,
                totalMonthlyInstallmentsToNonBanks: anyOutstandingDebts
                  ? parseInt(totalMonthlyInstallmentsToNonBanks)
                  : 0,
                totalRevolvingCreditOutstandingToBanks: anyOutstandingDebts
                  ? parseInt(totalRevolvingCreditOutstandingToBanks)
                  : 0,
                totalRevolvingCreditOutstandingToNonBanks: anyOutstandingDebts
                  ? parseInt(totalRevolvingCreditOutstandingToNonBanks)
                  : 0,
                currentLitigationAmount: anyOutstandingDebts
                  ? parseInt(currentLitigationAmount)
                  : 0,
                anyOutstandingDebts: anyOutstandingDebts,
                businessMobileNumber: application?.mobileNumber,
                businessEmailAddress: application?.businessOwnerEmail,
              },
              where: {
                id: application?.loanEligibility?.id,
              },
            },
          })
        }
        onSuccessUpdateApplicaton.reset()
        dispatch(fetchStop())
      } else {
      }

      if (onSuccessUpdateApplicaton.error) {
        const { networkError } = onSuccessUpdateApplicaton.error
        let errorList = 'ERROR: '
        if (networkError && (networkError as ServerError)) {
          errorList =
            errorList +
            (networkError as ServerError as any).result.errors
              .map((error: any) => error.message)
              .join(', ')
        }
        setShowAlert(true)
        setAlertMsg(errorList)
        setAlertType('primary')
      }
    }
  }, [
    onSuccessUpdateApplicaton.loading,
    onSuccessUpdateApplicaton.data,
    onSuccessUpdateApplicaton.error,
    onSuccessUpdateApplicaton.called,
  ])

  useEffect(() => {
    if (loanApplication !== null) {
      setMobileNo(loanApplication.mobileNumber)
    }
  }, [loanApplication])

  useEffect(() => {
    const countryCode = CountryList.filter(
      (country) => country.name === formik.values.mibCountryOfIncorporation
    )[0]?.code
    setCountryOfIncorporationCode(countryCode)
  }, [formik.values.mibCountryOfIncorporation])

  // const { id } = useParams<{ id: string }>()

  function backLink() {
    history.push(`/agent-application/view/dashboard`)
  }

  return (
    <div className='d-flex flex-column flex-column-fluid'>
      <style>{calendarIconStyles}</style>
      <MaintenanceModeBanner />

      <form onSubmit={formik.handleSubmit} noValidate>
        <div className='cardNew'>
          <div className='section-heading'>
            <h2>Loan Details</h2>
            <hr className='text-gray-400' />
          </div>

          <div className='row'>
            <div className='col-md-6'>
              <label htmlFor='' className='input-field-label required'>
                I want to borrow
              </label>
              <NumberFormat
                name='amountRequested'
                value={formik.values.amountRequested}
                className='form-control form-control-solid'
                // displayType={'text'}
                thousandSeparator={true}
                prefix={'SG$'}
                allowNegative={true} // Explicitly allow negative values
                isNumericString={true} // Treat the input as a numeric string
                onValueChange={(values: any) => {
                  let { value } = values
                  // Ensure we accept any value including negative
                  formik.setFieldValue('amountRequested', value)
                }}
                onBlur={(event: any) => {
                  let { value } = event.target
                  // For negative values, we need special handling since the prefix is at the beginning
                  // Check if the value contains a negative sign
                  const isNegative = event.target.value.includes('-')

                  // Remove the prefix (SG$) and any commas
                  if (isNegative) {
                    // For negative values, the format might be -SG$200,000 or SG$-200,000
                    value = value.replace(/[SG$,]/g, '')
                  } else {
                    value = value.substring(3) // Remove prefix
                    value = value.replace(/[,$]/g, '') // Remove commas and any dollar signs
                  }

                  let parsedValue = value ? parseFloat(value) : 0

                  // Always keep the original entered value, including negative values
                  formik.setFieldValue('amountRequested', parsedValue)
                  // Show appropriate validation errors
                  if (Number(parsedValue) < 0) {
                    formik.setFieldError('amountRequested', 'Amount cannot be negative')
                    formik.setFieldValue('amountRequested', parsedValue)
                  } else if (Number(parsedValue) === 0) {
                    formik.setFieldError('amountRequested', 'Amount cannot be zero')
                    formik.setFieldValue('amountRequested', parsedValue)
                  } else if (Number(parsedValue) < minAmount) {
                    formik.setFieldError(
                      'amountRequested',
                      `Amount cannot be less than SG$${minAmount.toLocaleString()}`
                    )
                    formik.setFieldValue('amountRequested', parsedValue)
                  } else if (Number(parsedValue) > maxAmount) {
                    formik.setFieldError(
                      'amountRequested',
                      `Amount cannot exceed SG$${maxAmount.toLocaleString()}`
                    )
                    formik.setFieldValue('amountRequested', parsedValue)
                  } else {
                    // Only round valid values
                    let roundedValue = Math.round(Number(parsedValue) / 500) * 500
                    formik.setFieldValue('amountRequested', roundedValue)
                  }

                  formik.setFieldTouched('amountRequested', true)
                }}
              />
              {formik.touched.amountRequested && formik.errors.amountRequested && (
                <div className='text-danger mt-2'>{formik.errors.amountRequested}</div>
              )}
              <RangeSlider
                // id="amountRequested"
                min={minAmount}
                max={maxAmount}
                step={5000}
                tooltip='on'
                tooltipPlacement='top'
                tooltipLabel={(currentValue) => `SG$ ${currentValue}`}
                value={formik.values.amountRequested}
                onChange={(event: any) => {
                  handleLoanAmountRange(event.target.value)
                }}
                className='rangeSlider'
                // onChange={({target:{value}}) => { if(regexNumber.test(value)) formik.setFieldValue('total', value) }}
                // onChange={formik.handleChange}
              />
              <div className='d-flex flex-row justify-content-between'>
                <div>SG$ 100,000</div>
                <div>SG$ 3,000,000</div>
              </div>
            </div>
            <div className='col-md-6'>
              <label htmlFor='' className='input-field-label required'>
                How long for?
              </label>
              <input
                name='durationInMonths'
                type='text'
                className='form-control form-control-solid'
                value={
                  formik.values.durationInMonths
                    ? `${formik.values.durationInMonths} ${
                        formik.values.durationInMonths === 1 ? 'Month' : 'Months'
                      }`
                    : ''
                }
                onChange={(e) => {
                  // Extract numbers from input
                  const value = e.target.value.replace(/\D/g, '')
                  if (value === '') {
                    formik.setFieldValue('durationInMonths', '')
                    return
                  }
                  const numValue = parseInt(value)
                  if (!isNaN(numValue)) {
                    formik.setFieldValue('durationInMonths', numValue)
                  }
                }}
                onBlur={(e) => {
                  let value = parseInt(e.target.value.replace(/\D/g, '') || '0')
                  value = Math.min(maxDuration, Math.max(minDuration, value || minDuration))
                  formik.setFieldValue('durationInMonths', value)
                }}
                onFocus={(e) => {
                  // Show only the number when focused for easier editing
                  const value = formik.values.durationInMonths
                  if (value) {
                    e.target.value = value.toString()
                  }
                }}
              />
              {formik.touched.durationInMonths && formik.errors.durationInMonths && (
                <div className='text-danger mt-2'>{formik.errors.durationInMonths}</div>
              )}
              <RangeSlider
                // id="durationInMonths"
                min={3}
                max={36}
                tooltip='on'
                tooltipPlacement='top'
                tooltipLabel={(currentValue) => `${currentValue} months`}
                value={formik.values.durationInMonths}
                // onChange={formik.handleChange}
                onChange={(event: any) => {
                  handleLoanDurationRange(event.target.value)
                }}
                className='rangeSlider'
              />
              <div className='d-flex flex-row justify-content-between'>
                <div>3 months</div>
                <div>36 months</div>
              </div>
            </div>
            <div className='col-md-12 mt-3'>
              <label htmlFor='' className='input-field-label required'>
                Loan Purpose
              </label>
              <div className='col-sm-12'>
                <select
                  name='loanPurpose'
                  className='form-select form-control-lg  border border-1'
                  value={formik.values.loanPurpose}
                  onChange={formik.handleChange}
                >
                  <option value='-'>---- Select Purpose ----</option>
                  {loanPurpose.map(({ label, value }: any, index: number) => (
                    <option key={index} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                {formik.touched.loanPurpose && formik.errors.loanPurpose && (
                  <div className='text-danger mt-2'>{formik.errors.loanPurpose}</div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className='cardNew'>
          <div className='section-heading'>
            <h2>Company Details</h2>
            <hr className='text-gray-400' />
          </div>
          <div className='row'>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Company UEN
              </label>
              <input
                name='companyUEN'
                type='text'
                className='form-control form-control-solid'
                value={formik.values.companyUEN}
                onChange={formik.handleChange}
              />
              {formik.touched.companyUEN && formik.errors.companyUEN && (
                <div className='text-danger mt-2'>{formik.errors.companyUEN}</div>
              )}
            </div>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Company Name
              </label>
              <input
                name='companyName'
                type='text'
                className='form-control form-control-solid'
                value={formik.values.companyName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.companyName && formik.errors.companyName && (
                <div className='text-danger mt-2'>{formik.errors.companyName}</div>
              )}
            </div>

            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Date of Incorporation
              </label>
              <div className='col-sm-12'>
                <DatePicker
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={100}
                  dropdownMode='select'
                  showMonthDropdown
                  dateFormat={'MM-dd-yyyy'}
                  placeholderText='MM-DD-YYYY'
                  onKeyDown={(e) => e.preventDefault()}
                  showIcon
                  // Custom styles to position the icon
                  customInput={
                    <input
                      className='form-control form-control-lg form-control-solid'
                      style={{ position: 'relative' }}
                    />
                  }
                  // Move icon to right side and center vertically
                  calendarIconClassname='calendar-icon-right'
                  icon={
                    <svg
                      width='15'
                      height='16'
                      viewBox='0 0 15 16'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                    >
                      <path
                        fill-rule='evenodd'
                        clip-rule='evenodd'
                        d='M3.93114 0C4.30452 0 4.6072 0.302681 4.6072 0.676056V1.62754H9.76526V0.676056C9.76526 0.302681 10.0679 0 10.4413 0C10.8147 0 11.1174 0.302681 11.1174 0.676056V1.62754H12.0689C12.6798 1.62754 13.2657 1.87024 13.6977 2.30225C14.1298 2.73426 14.3725 3.32019 14.3725 3.93114V13.6964C14.3725 14.3074 14.1298 14.8933 13.6977 15.3253C13.2657 15.7573 12.6798 16 12.0689 16H2.3036C1.69265 16 1.10672 15.7573 0.674709 15.3253C0.2427 14.8933 0 14.3074 0 13.6964V3.93114C0 3.32019 0.2427 2.73426 0.674709 2.30225C1.10672 1.87024 1.69265 1.62754 2.3036 1.62754H3.25509V0.676056C3.25509 0.302681 3.55777 0 3.93114 0ZM3.25509 2.97966H2.3036C2.05125 2.97966 1.80923 3.0799 1.6308 3.25834C1.45236 3.43678 1.35211 3.67879 1.35211 3.93114V6.51017H13.0203V3.93114C13.0203 3.67879 12.9201 3.43678 12.7417 3.25834C12.5632 3.0799 12.3212 2.97966 12.0689 2.97966H11.1174V3.93114C11.1174 4.30452 10.8147 4.6072 10.4413 4.6072C10.0679 4.6072 9.76526 4.30452 9.76526 3.93114V2.97966H4.6072V3.93114C4.6072 4.30452 4.30452 4.6072 3.93114 4.6072C3.55777 4.6072 3.25509 4.30452 3.25509 3.93114V2.97966ZM13.0203 7.86228H1.35211V13.6964C1.35211 13.9488 1.45236 14.1908 1.6308 14.3692C1.80924 14.5476 2.05125 14.6479 2.3036 14.6479H12.0689C12.3212 14.6479 12.5632 14.5476 12.7417 14.3692C12.9201 14.1908 13.0203 13.9488 13.0203 13.6964V7.86228ZM5.6964 10.4413C5.6964 10.0679 5.99908 9.76526 6.37246 9.76526H7.18623C7.5596 9.76526 7.86228 10.0679 7.86228 10.4413V12.8826C7.86228 13.256 7.5596 13.5587 7.18623 13.5587C6.81285 13.5587 6.51017 13.256 6.51017 12.8826V11.1174H6.37246C5.99908 11.1174 5.6964 10.8147 5.6964 10.4413Z'
                        fill='#191919'
                      />
                    </svg>
                  }
                  // Add inline styles for calendar icon
                  wrapperClassName='date-picker-wrapper'
                  required={true}
                  maxDate={today}
                  selected={formik.values.dateOfIncorporation}
                  onChangeRaw={(event: any) => {
                    // Handle manual text entry
                    const inputValue = event.target.value
                    const inputDate = new Date(inputValue)

                    // First check if the date is valid
                    if (isNaN(inputDate.getTime())) {
                      formik.setFieldError(
                        'dateOfIncorporation',
                        'Please enter valid Date of incorporation in MM/DD/YYYY format'
                      )
                      return
                    }

                    // Then check if date is in the future
                    if (inputDate > today) {
                      formik.setFieldError('dateOfIncorporation', 'Date cannot be in the future')
                      formik.setFieldValue('dateOfIncorporation', '')
                      // Prevent the onChange handler from firing with this value
                      event.preventDefault()
                      // Reset the value in the input field to the previous valid value
                      event.target.value = formik.values.dateOfIncorporation
                        ? formik.values.dateOfIncorporation.toLocaleDateString()
                        : ''
                    }
                  }}
                  onChange={(value: any) => {
                    // Check if selected date is in the future
                    if (value && value > today) {
                      formik.setFieldError('dateOfIncorporation', 'Date cannot be in the future')
                      formik.setFieldValue('dateOfIncorporation', '')
                      // Prevent the onChange handler from firing with this value
                      return
                    }
                    formik.setFieldValue('dateOfIncorporation', value)
                  }}
                />

                {formik.touched.dateOfIncorporation && formik.errors.dateOfIncorporation && (
                  <div className='text-danger mt-2'>{formik.errors.dateOfIncorporation} &nbsp;</div>
                )}
              </div>
            </div>

            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Country of Incorporation
              </label>
              <Autocomplete
                size='small'
                id='tags-outlined'
                className='countryList mt-2'
                popupIcon={<KeyboardArrowDownOutlinedIcon />}
                options={CountryList}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                value={formik.values.mibCountryOfIncorporation || null}
                onChange={(event, newValue) => {
                  formik.setFieldValue('mibCountryOfIncorporation', newValue?.name || '')
                }}
                renderOption={(props, option) => {
                  const { ...optionProps } = props
                  return (
                    <Box
                      component='li'
                      sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                      {...optionProps}
                    >
                      <img
                        loading='lazy'
                        width='20'
                        srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                        src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                        alt=''
                      />
                      {option.name}
                    </Box>
                  )
                }}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: countryOfIncorporationCode ? (
                          <img
                            loading='lazy'
                            width='25'
                            style={{ marginRight: 8 }}
                            src={`https://flagcdn.com/w20/${countryOfIncorporationCode.toLowerCase()}.png`}
                            alt=''
                          />
                        ) : null,
                      }}
                      placeholder='Select Country'
                    />
                  )
                }}
                isOptionEqualToValue={(option, value) => option.name === value.name}
              />

              {formik.touched.mibCountryOfIncorporation &&
                formik.errors.mibCountryOfIncorporation && (
                  <div className='text-danger mt-2'>{formik.errors.mibCountryOfIncorporation}</div>
                )}
            </div>

            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Industry
              </label>
              <div className='col-sm-12'>
                <select
                  name='industryType'
                  className='form-select form-control-lg  border border-1'
                  value={formik.values.industryType}
                  onChange={formik.handleChange}
                >
                  <option value='-'>---- Select Industry option ----</option>
                  {industryOptions.map(({ label, value }: any, index: number) => (
                    <option key={index} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
                {formik.touched.industryType && formik.errors.industryType && (
                  <div className='text-danger mt-2'>{formik.errors.industryType}</div>
                )}
              </div>
            </div>

            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Business Owner Name{' '}
                <OverlayTrigger
                  trigger='hover'
                  overlay={
                    <Popover>
                      <Popover.Body
                        style={{
                          backgroundColor: '#202020',
                          color: 'white',
                          borderRadius: '10px',
                          padding: '15px',
                        }}
                      >
                        Kindly provide the full name of the business owner (not the company name)
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <svg
                    width='14'
                    height='14'
                    viewBox='0 0 14 14'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M7 5H7.00667M6.33333 7H7V9.66667H7.66667M1 7C1 7.78793 1.15519 8.56815 1.45672 9.2961C1.75825 10.0241 2.20021 10.6855 2.75736 11.2426C3.31451 11.7998 3.97595 12.2417 4.7039 12.5433C5.43185 12.8448 6.21207 13 7 13C7.78793 13 8.56815 12.8448 9.2961 12.5433C10.0241 12.2417 10.6855 11.7998 11.2426 11.2426C11.7998 10.6855 12.2417 10.0241 12.5433 9.2961C12.8448 8.56815 13 7.78793 13 7C13 5.4087 12.3679 3.88258 11.2426 2.75736C10.1174 1.63214 8.5913 1 7 1C5.4087 1 3.88258 1.63214 2.75736 2.75736C1.63214 3.88258 1 5.4087 1 7Z'
                      stroke='var(--primary-color)'
                      stroke-width='1.5'
                      stroke-linecap='round'
                      stroke-linejoin='round'
                    />
                  </svg>
                </OverlayTrigger>
              </label>
              <input
                name='applicantName'
                type='text'
                className='form-control form-control-solid'
                value={formik.values.applicantName}
                onChange={formik.handleChange}
              />
              {formik.touched.applicantName && formik.errors.applicantName && (
                <div className='text-danger mt-2'>{formik.errors.applicantName}</div>
              )}
            </div>
          </div>
          <div className='row'>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Business Mobile Number
              </label>
              <PhoneInput
                id='mobileNumber'
                international
                defaultCountry='SG'
                value={formik.values.mobileNumber}
                onChange={(value) => formik.setFieldValue('mobileNumber', value)}
                placeholder='Enter phone number'
                className='int-phone-input'
              />
              {(formik.touched.mobileNumber || formik.values.mobileNumber === undefined) &&
                formik.errors.mobileNumber && (
                  <div className='text-danger mt-2'>{formik.errors.mobileNumber}</div>
                )}
            </div>
            <div className='col-md-6 mb-5'>
              {' '}
              <label htmlFor='' className='input-field-label required'>
                Business Owner Email{' '}
              </label>
              <input
                name='businessOwnerEmail'
                type='text'
                className='form-control form-control-solid'
                value={formik.values.businessOwnerEmail}
                onChange={formik.handleChange}
              />
              {formik.touched.businessOwnerEmail && formik.errors.businessOwnerEmail && (
                <div className='text-danger mt-2'>{formik.errors.businessOwnerEmail}</div>
              )}
            </div>
          </div>
        </div>

        <div className='cardNew'>
          <div className='section-heading'>
            <h2>Declare your Financials</h2>
            <hr className='text-gray-400' />
          </div>
          <div className='row mb-2'>
            <div className='col-md-6 mb-5'>
              <div className='row'>
                <label htmlFor='' className='input-field-label required'>
                  Annual Turnover
                </label>
                <div className='col-sm-12'>
                  <NumberFormat
                    name='turnover'
                    value={formik.values.turnover}
                    className='form-control form-control-lg form-control-solid'
                    thousandSeparator={true}
                    prefix={'SG$ '}
                    onValueChange={(values: any) => {
                      const { value } = values
                      formik.setFieldValue('turnover', value)
                    }}
                  />
                  {formik.touched.turnover && formik.errors.turnover && (
                    <div className='text-danger mt-2'>{formik.errors.turnover}</div>
                  )}
                </div>
              </div>
            </div>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Net Operating Income (profit)
              </label>
              <div className='col-sm-12'>
                <NumberFormat
                  name='profit'
                  value={formik.values.profit}
                  className='form-control form-control-lg form-control-solid'
                  thousandSeparator={true}
                  prefix={'SG$ '}
                  onValueChange={(values: any) => {
                    const { value } = values
                    formik.setFieldValue('profit', value)
                  }}
                />
                {formik.touched.profit && formik.errors.profit && (
                  <div className='text-danger mt-2'>{formik.errors.profit}</div>
                )}
              </div>
            </div>
            <div className='col-md-6 mb-5'>
              <label htmlFor='' className='input-field-label required'>
                Any Outstanding Loans/Debts
              </label>
              <div className='col-sm-12'>
                <div className='mb-10 d-flex'>
                  <div className='form-check form-check-custom form-check-solid me-10'>
                    <input
                      name='anyOutstandingDebts'
                      type='radio'
                      className='form-check-input'
                      checked={formik.values.anyOutstandingDebts}
                      onChange={() => formik.setFieldValue('anyOutstandingDebts', true)}
                    />
                    <label className='form-check-label'>Yes</label>
                  </div>
                  <div className='form-check form-check-custom form-check-solid'>
                    <input
                      name='anyOutstandingDebts'
                      type='radio'
                      className='form-check-input'
                      checked={!formik.values.anyOutstandingDebts}
                      onChange={() => formik.setFieldValue('anyOutstandingDebts', false)}
                    />
                    <label className='form-check-label'>No</label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {formik.values.anyOutstandingDebts && (
            <>
              <div className='row mb-1'>
                {formik.values.anyOutstandingDebts &&
                  formik.values.totalMonthlyInstallmentsToBanks <= 0 &&
                  formik.values.totalMonthlyInstallmentsToNonBanks <= 0 &&
                  formik.values.totalRevolvingCreditOutstandingToBanks <= 0 &&
                  formik.values.totalRevolvingCreditOutstandingToNonBanks <= 0 &&
                  formik.values.currentLitigationAmount <= 0 && (
                    <div className='text-danger'>
                      At least one field below should be greater than 0
                    </div>
                  )}
              </div>
              <div className='row mb-10'>
                <div className='col-md-6 mb-5'>
                  <label htmlFor='' className='input-field-label'>
                    Total Monthly Installments to banks
                  </label>
                  <div className='col-sm-12'>
                    <NumberFormat
                      name='totalMonthlyInstallmentsToBanks'
                      value={
                        formik.values.anyOutstandingDebts
                          ? formik.values.totalMonthlyInstallmentsToBanks
                          : '0.00'
                      }
                      className='form-control form-control-lg form-control-solid'
                      thousandSeparator={true}
                      prefix={'SG$ '}
                      onValueChange={(values: any) => {
                        const { value } = values
                        formik.setFieldValue('totalMonthlyInstallmentsToBanks', value)
                      }}
                    />
                  </div>
                  {formik.touched.totalMonthlyInstallmentsToBanks &&
                    formik.errors.totalMonthlyInstallmentsToBanks && (
                      <div className='text-danger mt-2'>
                        {formik.errors.totalMonthlyInstallmentsToBanks}
                      </div>
                    )}
                </div>
                <div className='col-md-6 mb-5'>
                  <label htmlFor='' className='input-field-label'>
                    Total Monthly Installments to non-banks
                  </label>
                  <div className='col-sm-12'>
                    <NumberFormat
                      name='totalMonthlyInstallmentsToNonBanks'
                      value={
                        formik.values.anyOutstandingDebts
                          ? formik.values.totalMonthlyInstallmentsToNonBanks
                          : '0.00'
                      }
                      className='form-control form-control-lg form-control-solid'
                      thousandSeparator={true}
                      prefix={'SG$ '}
                      onValueChange={(values: any) => {
                        const { value } = values
                        formik.setFieldValue('totalMonthlyInstallmentsToNonBanks', value)
                      }}
                    />
                    {formik.touched.totalMonthlyInstallmentsToNonBanks &&
                      formik.errors.totalMonthlyInstallmentsToNonBanks && (
                        <div className='text-danger mt-2'>
                          {formik.errors.totalMonthlyInstallmentsToNonBanks}
                        </div>
                      )}
                  </div>
                </div>
                <div className='col-md-6 mb-5'>
                  <label htmlFor='' className='input-field-label'>
                    Total Revolving Credit outstanding to banks
                  </label>
                  <div className='col-sm-12'>
                    <NumberFormat
                      name='totalRevolvingCreditOutstandingToBanks'
                      value={
                        formik.values.anyOutstandingDebts
                          ? formik.values.totalRevolvingCreditOutstandingToBanks
                          : '0.00'
                      }
                      className='form-control form-control-lg form-control-solid'
                      thousandSeparator={true}
                      prefix={'SG$ '}
                      onValueChange={(values: any) => {
                        const { value } = values
                        formik.setFieldValue('totalRevolvingCreditOutstandingToBanks', value)
                      }}
                    />
                    {formik.touched.totalRevolvingCreditOutstandingToBanks &&
                      formik.errors.totalRevolvingCreditOutstandingToBanks && (
                        <div className='text-danger mt-2'>
                          {formik.errors.totalRevolvingCreditOutstandingToBanks}
                        </div>
                      )}
                  </div>
                </div>

                <div className='col-md-6 mb-5'>
                  <label htmlFor='' className='input-field-label'>
                    Total Revolving Credit outstanding to non-banks
                  </label>
                  <div className='col-sm-12'>
                    <NumberFormat
                      name='totalRevolvingCreditOutstandingToNonBanks'
                      value={
                        formik.values.anyOutstandingDebts
                          ? formik.values.totalRevolvingCreditOutstandingToNonBanks
                          : '0.00'
                      }
                      className='form-control form-control-lg form-control-solid'
                      thousandSeparator={true}
                      prefix={'SG$ '}
                      onValueChange={(values: any) => {
                        const { value } = values
                        formik.setFieldValue('totalRevolvingCreditOutstandingToNonBanks', value)
                      }}
                    />
                    {formik.touched.totalRevolvingCreditOutstandingToNonBanks &&
                      formik.errors.totalRevolvingCreditOutstandingToNonBanks && (
                        <div className='text-danger mt-2'>
                          {formik.errors.totalRevolvingCreditOutstandingToNonBanks}
                        </div>
                      )}
                  </div>
                </div>
                <div className='col-md-6 mb-5'>
                  <label htmlFor='' className='input-field-label'>
                    Current Litigation Amount
                  </label>
                  <div className='col-sm-12'>
                    <NumberFormat
                      name='currentLitigationAmount'
                      value={
                        formik.values.anyOutstandingDebts
                          ? formik.values.currentLitigationAmount
                          : 0
                      }
                      className='form-control form-control-lg form-control-solid'
                      thousandSeparator={true}
                      prefix={'SG$ '}
                      onValueChange={(values: any) => {
                        const { value } = values
                        formik.setFieldValue('currentLitigationAmount', value)
                      }}
                    />
                    {formik.touched.currentLitigationAmount &&
                      formik.errors.currentLitigationAmount && (
                        <div className='text-danger mt-2'>
                          {formik.errors.currentLitigationAmount}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div style={{ marginBottom: '30px' }}>
          <div className='cardNew' style={{ marginBottom: '0px' }}>
            <div className='section-heading'>
              <h2>Channel Partner Consent</h2>
              <hr className='text-gray-400' />
            </div>
            <div>
              <label className='form-check form-check-inline form-check-solid me-5 mb-4'>
                <input
                  name='confirmCheckbox'
                  type='checkbox'
                  style={{ color: '#19147B' }}
                  id='form-check-input'
                  className={`form-check-input ${
                    (formik.submitCount > 0 ||
                      formik.errors.confirmCheckbox ||
                      formik.errors.confirmCheckboxConsent) &&
                    !formik.values.confirmCheckbox
                      ? 'is-invalid'
                      : ''
                  }`}
                  checked={formik.values.confirmCheckbox}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <span className='ps-2 fs-4 text-justified'>
                  I hereby confirm that I have consent from{' '}
                  {Boolean(formik.values.companyName)
                    ? formik.values.companyName
                    : '[Company Name]'}{' '}
                  to submit this loan application on their behalf.
                </span>
              </label>
              <label className='form-check form-check-inline form-check-solid me-5 mb-4'>
                <input
                  name='validDataCheckbox'
                  type='checkbox'
                  id='form-check-input'
                  className={`form-check-input ${
                    (formik.submitCount > 0 ||
                      formik.errors.validDataCheckbox ||
                      formik.errors.confirmCheckboxConsent ||
                      formik.errors.validDataCheckbox) &&
                    !formik.values.validDataCheckbox
                      ? 'is-invalid'
                      : ''
                  }`}
                  checked={formik.values.validDataCheckbox}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <span className='ps-2 fs-4 text-justified'>
                  The applicant affirms that, to the best of their knowledge that the information
                  provided is accurate, complete, and correct.
                </span>
              </label>

              <label className='form-check form-check-inline form-check-solid me-5 mb-4'>
                <input
                  name='confirmCheckboxConsent'
                  type='checkbox'
                  id='form-check-input'
                  className={`form-check-input ${
                    (formik.submitCount > 0 ||
                      formik.errors.confirmCheckbox ||
                      formik.errors.confirmCheckboxConsent) &&
                    !formik.values.confirmCheckboxConsent
                      ? 'is-invalid'
                      : ''
                  }`}
                  checked={formik.values.confirmCheckboxConsent}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <span className='ps-2 fs-4 text-justified'>
                  I accept and acknowledge that all data privacy laws and the CrediLinq Privacy
                  Policy which can be found [
                  <a
                    href={privacyPolicy()}
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{ fontWeight: 500, textDecoration: 'underline', color: '#19147B' }}
                  >
                    here
                  </a>
                  ] are binding to me.
                </span>
              </label>
            </div>
          </div>
        </div>
        <div className='text-danger border-text'>
          {(formik.touched.confirmCheckbox ||
            formik.touched.validDataCheckbox ||
            formik.touched.confirmCheckboxConsent) &&
            (formik.errors.confirmCheckbox ||
              formik.errors.validDataCheckbox ||
              formik.errors.confirmCheckboxConsent) && (
              <div>
                {formik.errors.confirmCheckbox ||
                  formik.errors.validDataCheckbox ||
                  formik.errors.confirmCheckboxConsent}
              </div>
            )}
        </div>

        <div className='cardNew stickeyFooter'>
          <div className={`d-flex justify-content-between`}>
            <button type='button' className='button button-outline-back' onClick={backLink}>
              <LeftArowIcon color='currentColor' fontSize={12} />
              Back
            </button>
            <button
              type='submit'
              id='button-primary'
              className='button button-primary'
              disabled={formik.isSubmitting}
              onClick={() => {
                scrollToErros()
              }}
            >
              {formik.isSubmitting ? 'Sending...' : 'Save and Continue'}{' '}
              <RightAeroIcon color='currentColor' />
            </button>
          </div>
        </div>
      </form>

      {showAlert && (
        <Alert variant={alertType} showAlert={showAlert} setShowAlert={setShowAlert}>
          {alertMsg}
        </Alert>
      )}
    </div>
  )
}

export { Step1Sub1 }
